import * as AccordionPrimitive from "@rn-primitives/accordion";
import { ChevronDown } from "lucide-react-native";
import { Platform, Pressable, Text, View } from "react-native";
import Animated, {
  FadeOutUp,
  LayoutAnimationConfig,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from "react-native-reanimated";
import { twMerge } from "tailwind-merge";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { colors } from "../theme/tokens";

export function Accordion({
  children,
  ...props
}: Omit<AccordionPrimitive.RootProps, "asChild"> &
  React.RefAttributes<AccordionPrimitive.RootRef>) {
  return (
    <LayoutAnimationConfig skipEntering>
      <AccordionPrimitive.Root
        asChild={Platform.OS !== "web"}
        {...(props as AccordionPrimitive.RootProps)}
      >
        <Animated.View layout={LinearTransition.duration(200)}>
          {children}
        </Animated.View>
      </AccordionPrimitive.Root>
    </LayoutAnimationConfig>
  );
}

export function AccordionItem({
  value,
  children,
  className,
  ...props
}: AccordionPrimitive.ItemProps &
  React.RefAttributes<AccordionPrimitive.ItemRef>) {
  return (
    <AccordionPrimitive.Item
      value={value}
      asChild
      // No rule between items: Moon cards separate rows by space.
      className={className}
      {...props}
    >
      <Animated.View
        layout={Platform.select({ native: LinearTransition.duration(200) })}
        className="native:overflow-hidden"
      >
        {children}
      </Animated.View>
    </AccordionPrimitive.Item>
  );
}

const Trigger = Platform.OS === "web" ? View : Pressable;

/**
 * The item's chevron: down when closed, up when open. The trigger draws one
 * at its end; pass `chevron={false}` to the trigger and place this inside it
 * when the card's layout needs the chevron elsewhere.
 */
export function AccordionChevron() {
  const { isExpanded } = AccordionPrimitive.useItemContext();
  const progress = useDerivedValue(
    () =>
      isExpanded
        ? withTiming(1, { duration: 250 })
        : withTiming(0, { duration: 200 }),
    [isExpanded],
  );
  const chevronStyle = useAnimatedStyle(
    () => ({
      transform: [{ rotate: `${progress.value * 180}deg` }],
    }),
    [progress],
  );
  return (
    // As tall as a title line, so the chevron centres on the first line and
    // turns in place.
    <View style={{ height: 18, justifyContent: "center", flexShrink: 0 }}>
      <Animated.View style={chevronStyle}>
        <ChevronDown
          size={16}
          color={colors.textLight}
          strokeWidth={1.8}
          className={Platform.select({ web: "pointer-events-none" })}
        />
      </Animated.View>
    </View>
  );
}

export function AccordionTrigger({
  children,
  className,
  action,
  analyticsLabel,
  chevron = true,
  onPress,
  ...props
}: AccordionPrimitive.TriggerProps & {
  children?: React.ReactNode;
  /** Draw the chevron at the trigger's end. Off when `AccordionChevron` is
   * placed inside the children instead. */
  chevron?: boolean;
  /**
   * Stable analytics identifier for the toggle. Pass an `expand`/`collapse`
   * pair when a funnel needs to tell opening from closing.
   */
  action?: string | { expand: string; collapse: string };
  /**
   * The visible copy to report when `children` is not a plain string. Never
   * pass form values or personal details here.
   */
  analyticsLabel?: string;
} & React.RefAttributes<AccordionPrimitive.TriggerRef>) {
  const { isExpanded } = AccordionPrimitive.useItemContext();
  const capturePress = usePressCapture("AccordionTrigger");

  // Runs before the toggle re-renders, so `isExpanded` is still the state
  // the press acted on.
  const capture = () => {
    if (props.disabled) return;
    capturePress(
      pressLabel(
        analyticsLabel,
        children,
        props.accessibilityLabel,
        props.testID,
      ),
      typeof action === "string"
        ? action
        : action
          ? isExpanded
            ? action.collapse
            : action.expand
          : null,
    );
  };
  // Native: the primitive calls `onPress` after toggling. Web: the trigger
  // is a View, which React Native Web never hands an `onPress`, so the press
  // is read from the DOM click that Radix toggles on.
  const webPressProps = Platform.OS === "web" ? { onClick: capture } : {};

  return (
    <AccordionPrimitive.Header>
      <AccordionPrimitive.Trigger
        {...props}
        onPress={(event) => {
          if (Platform.OS !== "web") capture();
          onPress?.(event);
        }}
        asChild
      >
        <Trigger
          {...webPressProps}
          className={twMerge(
            "flex-row items-start justify-between gap-4 py-3 disabled:opacity-50",
            Platform.select({
              web: "flex flex-1 outline-none disabled:pointer-events-none",
            }),
            isExpanded && "pb-2",
            className,
          )}
        >
          {typeof children === "string" ? (
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: 18,
                fontWeight: "800",
                color: colors.text,
              }}
            >
              {children}
            </Text>
          ) : (
            <>{children}</>
          )}
          {chevron ? <AccordionChevron /> : null}
        </Trigger>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

export function AccordionContent({
  children,
  className,
  ...props
}: AccordionPrimitive.ContentProps &
  React.RefAttributes<AccordionPrimitive.ContentRef>) {
  const { isExpanded } = AccordionPrimitive.useItemContext();

  return (
    <AccordionPrimitive.Content
      className={twMerge(
        "overflow-hidden",
        Platform.select({
          web: isExpanded ? "animate-accordion-down" : "animate-accordion-up",
        }),
      )}
      {...props}
    >
      <Animated.View
        exiting={Platform.select({ native: FadeOutUp.duration(200) })}
        className={twMerge("pb-3", className)}
      >
        {children}
      </Animated.View>
    </AccordionPrimitive.Content>
  );
}
